import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { Layout } from '~/eds/src/components/Layout/Layout';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { Text } from '~/eds/src/components/Text/Text';
import { UserAction } from '~/eds/src/types/UserAction';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { api } from '~/redux/api/createApi';
import { sendAgentExecutionMessage } from '~/redux/api/methods/agent-execution/sendMessage';
import { mapUserRatingFromFeedbackDetails } from '~/redux/api/transformers/conversationalAi';
import { enhancedApi } from '~/redux/api/types/sylvanus/agentExecution';
import { openInNewTab } from '~/utils/browser';

import { Chat } from '../../chat/Chat';
import { getStaticContextActions } from '../../chatbot/utils';
import { FeedbackDetails, Message as MessageType } from '../../types/chat';
import { useAgenticAi } from '../useAgenticAi';
import { usePendingMessages } from './usePendingMessages';
import { useWorkflowRecommendations } from './useWorkflowRecommendations';
import { useWorkflowRoutingMessages } from './useWorkflowRoutingMessages';
import {
  AGENT_TYPE,
  AI_USER,
  buildWorkflowUrl,
  createMessageContext,
  createPendingMessage,
  createWorkflowRoutingExecutionConfig,
  EXECUTION_TAG,
} from './utils';
import { WorkflowRoutingMessageRenderer } from './WorkflowRoutingMessageRenderer';
import { WorkflowRoutingWelcome } from './WorkflowRoutingWelcome';

export interface WorkflowRecommendation {
  workflow_id: string;
  published_workflow_id: string | null;
  workflow_name: string;
  reasoning: string;
  confidence: 'high' | 'medium' | 'low';
}

export interface WorkflowRoutingAdditionalInfo {
  workflow_recommendations: WorkflowRecommendation[];
  no_match: boolean;
}

export interface WorkflowSuggestion {
  workflowId: string;
  publishedWorkflowId: string | null;
  workflowName: string;
  reasoning: string;
  confidence: 'high' | 'medium' | 'low';
  permanentLinkId: string;
  wssDesignated: boolean;
}

export interface WorkflowRoutingAgentProps {
  onClose?: () => void;
  panelProps?: Partial<React.ComponentProps<typeof Panel>>;
  onResetAgent?: () => void;
}

export const WorkflowRoutingAgent = ({
  panelProps,
  onResetAgent,
}: WorkflowRoutingAgentProps) => {
  const intl = useIntl();
  const currentUser = useCurrentUser();

  const {
    agentExecutionId,
    agentExecution,
    messages,
    streamMessage,
    isStreaming,
    thinkingEvents,
    isCreatingAgentExecution,
    isSendingMessage,
    hasFailedToLoadInitialSuggestions,
    createAgentExecution,
    sendMessage,
    interruptAgentExecution,
    resetAgentExecution,
  } = useAgenticAi({
    entityType: 'all_documents',
    executionTag: EXECUTION_TAG,
    enableAutoToasts: true,
  });

  const { data: routingConfig } =
    api.endpoints.getRoutingConfiguration.useQuery(undefined);

  const [updateMessage] =
    enhancedApi.useUpdateAeMessageApiV1AgentExecutionAeIdMessageMessageIdPatchMutation();

  const [isManuallyReset, setIsManuallyReset] = useState(false);
  const [hasCreationError, setHasCreationError] = useState(false);

  const {
    pendingMessages: pendingUserMessages,
    addPendingMessage,
    removePendingMessage,
    clearPendingMessages,
  } = usePendingMessages({ messages });

  const { workflows, storedThinkingEvents, fallbackMessage } =
    useWorkflowRecommendations({
      messages,
      streamMessage,
      thinkingEvents,
      isStreaming,
      isSendingMessage,
      fallbackResponse: routingConfig?.fallbackResponse || undefined,
    });

  useEffect(() => {
    if (
      !agentExecutionId &&
      !isStreaming &&
      !isCreatingAgentExecution &&
      !isManuallyReset &&
      !hasCreationError
    ) {
      handleAgentSelection().catch(() => {
        setHasCreationError(true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const effectiveMessages = agentExecutionId ? messages : [];

  const filteredMessages = useMemo(() => {
    if (
      effectiveMessages.length === 1 &&
      effectiveMessages[0].userType === 'other' &&
      effectiveMessages[0].responseSuggestions &&
      effectiveMessages[0].responseSuggestions.length > 0
    ) {
      return [];
    }

    return effectiveMessages;
  }, [effectiveMessages]);

  const { allMessagesWithWorkflows, workflowRoutingMessage } =
    useWorkflowRoutingMessages({
      messages: filteredMessages,
      pendingUserMessages,
      streamMessage,
      workflows,
      thinkingEvents,
      storedThinkingEvents,
      isStreaming,
      isSendingMessage,
      fallbackMessage,
    });
  const ensureAgentExecution = useCallback(async () => {
    if (agentExecutionId) {
      return agentExecutionId;
    }

    setIsManuallyReset(false);
    const execution = await createAgentExecution(
      AGENT_TYPE,
      createWorkflowRoutingExecutionConfig(),
    );
    return execution?.id ?? null;
  }, [agentExecutionId, createAgentExecution]);

  const sendAgentMessage = useCallback(
    async (text: string, contextType: 'query' | 'command') => {
      const executionId = await ensureAgentExecution();
      if (!executionId) {
        return;
      }

      const pendingMessage = createPendingMessage(text, contextType);
      addPendingMessage(pendingMessage);

      try {
        await sendMessage({
          message: text,
          context: createMessageContext(contextType),
          agentExecutionId: executionId,
        });
      } catch {
        removePendingMessage(pendingMessage.id);
      }
    },
    [ensureAgentExecution, sendMessage],
  );

  const handlePost = useCallback(
    async (text: string) => {
      await sendAgentMessage(text, 'query');
    },
    [sendAgentMessage],
  );

  const trackWorkflowClickAction = useCallback(
    async (workflow: WorkflowSuggestion) => {
      if (!agentExecutionId) {
        return;
      }

      try {
        await sendAgentExecutionMessage({
          agentExecutionId,
          message: 'link-followed',
          context: {
            messageType: 'action',
          },
          metadata: {
            entity_type: 'workflow',
            entity_id: workflow.workflowId,
            entity_name: workflow.workflowName,
          },
        });
      } catch {
        // No error handling for now as tracking should not interrupt user flow
      }
    },
    [agentExecutionId],
  );

  const handleWorkflowClick = useCallback(
    async (workflow: WorkflowSuggestion) => {
      const url = buildWorkflowUrl(
        workflow,
        currentUser.client_config.sub_domain,
      );
      openInNewTab(url);
      await trackWorkflowClickAction(workflow);
    },
    [currentUser, trackWorkflowClickAction],
  );

  const handleSubmitFeedback = useCallback(
    async (message: MessageType, feedbackDetails: FeedbackDetails) => {
      if (!agentExecutionId || !message.id) {
        return;
      }

      try {
        const userRating = mapUserRatingFromFeedbackDetails(feedbackDetails);

        await updateMessage({
          aeId: agentExecutionId,
          messageId: message.id as unknown as number,
          updateAgentExecutionMessageRequest: {
            user_rating: userRating,
            user_comment: feedbackDetails.comment || null,
          },
        }).unwrap();
      } catch (error) {
        console.error('Failed to submit feedback:', error);
      }
    },
    [agentExecutionId, updateMessage],
  );

  const handleChoose = useCallback(
    async (message: MessageType, choice: string | WorkflowSuggestion) => {
      if (typeof choice === 'object' && 'workflowId' in choice) {
        handleWorkflowClick(choice);
        return;
      }

      const choiceText =
        typeof choice === 'string'
          ? choice
          : (choice as WorkflowSuggestion).workflowName;

      await sendAgentMessage(choiceText, 'command');
    },
    [handleWorkflowClick, sendAgentMessage],
  );

  const handleAgentSelection = useCallback(async () => {
    if (!agentExecutionId) {
      try {
        setHasCreationError(false);
        await createAgentExecution(
          AGENT_TYPE,
          createWorkflowRoutingExecutionConfig(),
        );
      } catch (error) {
        setHasCreationError(true);
        throw error;
      }
    }
  }, [agentExecutionId, createAgentExecution]);

  const handleNewConversation = useCallback(async () => {
    clearPendingMessages();
    resetAgentExecution();
    setHasCreationError(false);

    if (onResetAgent) {
      onResetAgent();
    }

    if (agentExecutionId) {
      interruptAgentExecution().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentExecutionId, interruptAgentExecution, resetAgentExecution]);

  const mergedPanelProps = useMemo(() => {
    const newConversationAction: UserAction = {
      icon: 'new-conversation' as const,
      tooltip: intl.formatMessage({
        id: 'ChatBot.newConversationTooltip',
        defaultMessage: 'New Conversation',
        description: 'Tooltip for new conversation button',
      }),
      onClick: handleNewConversation,
      disabled: isCreatingAgentExecution || isSendingMessage,
      text: '',
    };

    return {
      ...panelProps,
      actions: [newConversationAction, ...(panelProps?.actions ?? [])],
    };
  }, [
    panelProps,
    handleNewConversation,
    isCreatingAgentExecution,
    isSendingMessage,
    intl,
  ]);

  const loadingContent = useMemo(
    () => ({
      isLoading:
        messages.length === 0 &&
        !hasCreationError &&
        (!agentExecutionId || isCreatingAgentExecution || isSendingMessage),
      message: intl.formatMessage({
        id: 'WorkflowRoutingAgent.loading',
        defaultMessage: 'Initializing agent…',
        description: 'Loading message for workflow routing agent',
      }),
    }),
    [
      messages.length,
      isCreatingAgentExecution,
      isSendingMessage,
      agentExecutionId,
      hasCreationError,
      intl,
    ],
  );

  const footer = (
    <Layout justify="center">
      <Text variant="tiny" color="text.quiet" fontWeight="500">
        {intl.formatMessage({
          id: 'WorkflowRoutingAgent.disclaimer',
          defaultMessage:
            'AI may occasionally be incorrect. Please verify important details before taking action.',
          description: 'Disclaimer footer for workflow routing agent',
        })}
      </Text>
    </Layout>
  );

  const workflowContextConfig = useMemo(() => {
    const contextActions = getStaticContextActions(intl, true, true);
    const activeContext = {
      icon: 'filter' as const,
      text: intl.formatMessage({
        id: 'ChatBotUtils.workflow',
        defaultMessage: 'Workflow',
        description: 'Option to search within workflow',
      }),
      onReset: undefined,
      disableReset: false,
    };

    return {
      activeContext,
      activeContextActions: contextActions,
    };
  }, [intl]);

  const replyMessage = useMemo(() => {
    if (workflowRoutingMessage) {
      return null;
    }

    if ((isStreaming || isSendingMessage) && streamMessage) {
      return {
        id: streamMessage.id,
        text: streamMessage.text,
        userType: streamMessage.userType,
        timestamp: streamMessage.timestamp,
        choices: streamMessage.responseSuggestions ?? [],
        citations: streamMessage.citations ?? [],
        sources: streamMessage.sources ?? [],
      };
    }

    return null;
  }, [workflowRoutingMessage, isStreaming, isSendingMessage, streamMessage]);

  const interruptAction = useMemo(
    () => ({
      icon: 'circle-stop' as const,
      onClick: interruptAgentExecution,
      text: intl.formatMessage({
        id: 'WorkflowRoutingAgent.stopGenerating',
        defaultMessage: 'Stop Generating',
        description: 'Stop generating action',
      }),
    }),
    [intl, interruptAgentExecution],
  );

  const initialSuggestions = useMemo(() => {
    if (agentExecution?.messages && agentExecution.messages.length > 0) {
      const firstMessage = agentExecution.messages[0];
      return firstMessage.responseSuggestions ?? null;
    }
    return null;
  }, [agentExecution]);

  return (
    <Chat
      disclaimer={{
        accept: {
          text: '',
        },
        hasAccepted: true,
        welcomeContent: {
          title: '',
          content: '',
        },
      }}
      emptyConversation={
        <WorkflowRoutingWelcome
          onChoose={handleChoose}
          suggestions={initialSuggestions}
          instructions={routingConfig?.instructions || undefined}
          initialSuggestionsFailedToLoad={hasFailedToLoadInitialSuggestions}
        />
      }
      footer={footer}
      replyMessage={replyMessage}
      isPosting={isStreaming || isSendingMessage}
      isPrivate={true}
      loadingContent={loadingContent}
      isLoadingReply={isStreaming}
      messages={allMessagesWithWorkflows}
      otherUser={AI_USER}
      title={intl.formatMessage({
        id: 'WorkflowRoutingAgent.title',
        defaultMessage: 'Workflow Routing Agent',
        description: 'Title of workflow routing agent',
      })}
      messageActions={[]}
      interruptAction={interruptAction}
      onChoose={handleChoose}
      onPost={handlePost}
      onSelectMessageSource={() => {}}
      onSubmitFeedback={handleSubmitFeedback}
      thinkingEvents={thinkingEvents}
      activeContext={workflowContextConfig.activeContext}
      activeContextActions={workflowContextConfig.activeContextActions}
      customMessageRenderer={(message, index, defaultProps) => (
        <WorkflowRoutingMessageRenderer
          message={message}
          index={index}
          defaultProps={{
            ...defaultProps,
            onWorkflowClick: handleWorkflowClick,
          }}
        />
      )}
      panelProps={mergedPanelProps}
    />
  );
};
